import {
  createContext,
  ReactNode,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  createTheme,
  StyledEngineProvider,
  ThemeProvider,
} from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import colors from "../shared/constants/color";
import {
  getWiserTheme,
  WISER_DARK_BODY_GRADIENT,
  WISER_THEME_STORAGE_KEY,
} from "../shared/theme/wiserTheme";

type ThemeContextType = {
  switchColorMode: () => void;
  darkModeActive: boolean;
};

type ThemeProviderProps = {
  children: ReactNode;
};

export const ThemeContext = createContext<ThemeContextType>({
  switchColorMode: () => {},
  darkModeActive: false,
});

const buildLightMuiTheme = () =>
  createTheme({
    palette: {
      mode: "light",
    },
  });

const buildDarkMuiTheme = () => {
  const tokens = getWiserTheme(true);

  return createTheme({
    palette: {
      mode: "dark",
      primary: {
        main: colors.deepBlue,
        light: colors.blue.B300,
        dark: colors.blue.B800,
      },
      secondary: {
        main: colors.secondary,
      },
      background: {
        default: tokens.pageBg,
        paper: tokens.surface,
      },
      text: {
        primary: tokens.textPrimary,
        secondary: tokens.textSecondary,
      },
      divider: tokens.border,
    },
    typography: {
      fontFamily:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: tokens.pageBg,
            backgroundImage: WISER_DARK_BODY_GRADIENT,
            backgroundAttachment: "fixed",
            color: tokens.textPrimary,
            transition: "background-color 0.2s ease, color 0.2s ease",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.inputBg,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: tokens.surfaceStrong,
            backgroundImage: "none",
            border: `1px solid ${tokens.border}`,
            boxShadow: tokens.surfaceShadow,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: tokens.surfaceStrong,
            backgroundImage: "none",
            border: `1px solid ${tokens.border}`,
            boxShadow: tokens.surfaceShadow,
          },
        },
      },
    },
  });
};

export function ThemeContextProvider({ children }: ThemeProviderProps) {
  const [mode, setMode] = useState<"light" | "dark">(() => {
    const savedMode = localStorage.getItem(WISER_THEME_STORAGE_KEY);
    return savedMode === "dark" ? "dark" : "light";
  });

  const switchColorMode = useCallback(() => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  }, []);

  const darkModeActive = mode === "dark";

  const theme = useMemo(
    () => (darkModeActive ? buildDarkMuiTheme() : buildLightMuiTheme()),
    [darkModeActive],
  );

  useEffect(() => {
    localStorage.setItem(WISER_THEME_STORAGE_KEY, mode);
    document.documentElement.setAttribute("data-theme", mode);
    if (darkModeActive) {
      document.documentElement.style.colorScheme = "dark";
    } else {
      document.documentElement.style.colorScheme = "light";
    }
  }, [mode, darkModeActive]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeContext.Provider value={{ switchColorMode, darkModeActive }}>
        <ThemeProvider theme={theme}>
          {darkModeActive && <CssBaseline enableColorScheme />}
          {children}
        </ThemeProvider>
      </ThemeContext.Provider>
    </StyledEngineProvider>
  );
}
